// Staffing Requirement Formula — pure computation. cfg = mergeST(content).

const C = Math.ceil;
const N = (v) => (v === "" || v === null || v === undefined || isNaN(Number(v)) ? 0 : Number(v));

export function computeStaffing(inp, cfg, adjust = null) {
  const f = cfg.formulas;
  const R = N(inp.rooms), S = N(inp.seats), O = N(inp.outdoor), G = N(inp.gates);
  const E = N(inp.actualCapacity) > 0 ? N(inp.actualCapacity) : Math.floor(O / (N(f.sqft_per_guest) || 18));
  const capacityEstimated = !(N(inp.actualCapacity) > 0);
  const opt = (cfg.complexity.options || []).find((o) => o.id === inp.complexityId) || { factor: 1, label: "Standard" };
  const factor = N(opt.factor) || 1;
  const excluded = new Set(opt.excluded_roles || []);
  const costF = N(f.cost_factor) || 1.22;
  const gross = (rid) => N((cfg.roles[rid] || {}).salary);
  const load = (rid) => Math.round(gross(rid) * costF);

  const duty = (R > f.duty1_rooms || S > f.duty1_seats || E > f.duty1_capacity ? 1 : 0) + (R > f.duty2_rooms || E > f.duty2_capacity ? 1 : 0);
  const frontOffice = f.fo_base + C(R / f.fo_rooms_per) + (E > f.fo_event_threshold ? 1 : 0);
  const security = C(G * f.sec_per_gate) + (O > f.sec_outdoor_threshold ? 1 : 0);
  const roomHK = C((R / f.hk_rooms_per) * f.hk_leave_factor);
  const hkSup = (R >= f.hks_rooms || O >= f.hks_outdoor ? 1 : 0) + (R > f.hks2_rooms || E > f.hks2_capacity ? 1 : 0);
  const publicHK = C((1 + O / f.pa_outdoor_per + S / f.pa_seats_per) * f.pa_leave_factor);
  const gardeners = Math.max(1, C((O / f.garden_per) * f.garden_factor));
  const engineering = Math.max(f.eng_min, C(R / f.eng_rooms_per) + C(O / f.eng_outdoor_per));
  const captains = Math.max(1, C(S / f.capt_per));
  const servers = C((S / f.server_per) * f.server_leave_factor);
  const cooks = Math.max(f.cook_min, C(S / f.cook_per));
  const helpers = Math.max(f.helper_min, C(S / f.helper_per));
  const stewards = Math.max(f.steward_min, C(S / f.steward_per));
  const stores = 1 + (R > f.stores_rooms || S > f.stores_seats ? 1 : 0);
  const laundry = R >= f.laundry_rooms ? 1 : 0;

  const adj = (n) => (factor === 1 || n === 0 ? n : Math.max(n > 0 ? 1 : 0, Math.round(n * factor)));

  const DEPTS = [
    { id: "leadership", label: "Property leadership", operational: false, roles: [["property_manager", 1], ["duty_manager", duty]],
      formula: `1 Property Manager + 1 Duty Manager if rooms > ${f.duty1_rooms} OR seats > ${f.duty1_seats} OR capacity > ${f.duty1_capacity}; +1 more if rooms > ${f.duty2_rooms} OR capacity > ${f.duty2_capacity}` },
    { id: "front_office", label: "Front office & guest relations", operational: true, roles: [["front_office_exec", frontOffice]],
      formula: `${f.fo_base} + CEILING(rooms ÷ ${f.fo_rooms_per}) + 1 if capacity > ${f.fo_event_threshold}` },
    { id: "security", label: "Security", operational: false, roles: [["security_guard", security]],
      formula: `CEILING(gates × ${f.sec_per_gate}) + 1 if outdoor area > ${Number(f.sec_outdoor_threshold).toLocaleString("en-IN")} sqft` },
    { id: "room_hk", label: "Room housekeeping", operational: true, roles: [["room_attendant", roomHK]],
      formula: `CEILING((rooms ÷ ${f.hk_rooms_per}) × ${f.hk_leave_factor})` },
    { id: "hk_supervisor", label: "Housekeeping supervisor", operational: false, roles: [["hk_supervisor", hkSup]],
      formula: `1 if rooms ≥ ${f.hks_rooms} OR outdoor ≥ ${Number(f.hks_outdoor).toLocaleString("en-IN")} sqft; +1 if rooms > ${f.hks2_rooms} OR capacity > ${f.hks2_capacity}` },
    { id: "public_hk", label: "Public-area housekeeping", operational: true, roles: [["public_area_attendant", publicHK]],
      formula: `CEILING((1 + outdoor ÷ ${Number(f.pa_outdoor_per).toLocaleString("en-IN")} + seats ÷ ${f.pa_seats_per}) × ${f.pa_leave_factor})` },
    { id: "gardening", label: "Gardening & outdoor upkeep", operational: true, roles: [["gardener", gardeners]],
      formula: `MAX(1, CEILING((outdoor ÷ ${Number(f.garden_per).toLocaleString("en-IN")}) × ${f.garden_factor}))` },
    { id: "engineering", label: "Engineering & maintenance", operational: true, roles: [["technician", engineering]],
      formula: `MAX(${f.eng_min}, CEILING(rooms ÷ ${f.eng_rooms_per}) + CEILING(outdoor ÷ ${Number(f.eng_outdoor_per).toLocaleString("en-IN")}))` },
    { id: "fnb", label: "Restaurant service (F&B)", operational: true, roles: [["fnb_captain", captains], ["server", servers]],
      formula: `Captains: MAX(1, CEILING(seats ÷ ${f.capt_per})) · Servers: CEILING((seats ÷ ${f.server_per}) × ${f.server_leave_factor})` },
    { id: "kitchen", label: "Kitchen & stewarding", operational: true, roles: [["chef_in_charge", 1], ["cook", cooks], ["kitchen_helper", helpers], ["steward", stewards]],
      formula: `1 Chef + Cooks MAX(${f.cook_min}, CEILING(seats ÷ ${f.cook_per})) + Helpers MAX(${f.helper_min}, CEILING(seats ÷ ${f.helper_per})) + Stewards MAX(${f.steward_min}, CEILING(seats ÷ ${f.steward_per}))` },
    { id: "stores", label: "Stores & administration", operational: false, roles: [["stores_admin", stores]],
      formula: `1 + 1 if rooms > ${f.stores_rooms} OR seats > ${f.stores_seats}` },
    { id: "laundry", label: "Laundry & linen coordination", operational: false, roles: [["laundry_coordinator", laundry]],
      formula: `1 if rooms ≥ ${f.laundry_rooms} (laundry outsourced by default)` },
  ];

  const departments = DEPTS.map((d) => {
    const roles = d.roles
      .map(([rid, base]) => {
        const key = `${d.id}:${rid}`;
        const ov = (adjust && adjust.roles && adjust.roles[key]) || {};
        const removed = !!(adjust && Array.isArray(adjust.removed) && adjust.removed.includes(key));
        let count = d.operational ? adj(base) : base;
        if (excluded.has(rid)) count = 0;
        if (ov.count !== undefined && ov.count !== "") count = Math.max(0, Math.round(N(ov.count)));
        if (removed) count = 0;
        const g = ov.salary !== undefined && ov.salary !== "" ? N(ov.salary) : gross(rid);
        const loaded = Math.round(g * costF);
        return { id: rid, key, label: (cfg.roles[rid] || {}).label || rid, count, gross: g, loaded, monthly: count * loaded, overridden: !!(ov.count !== undefined || ov.salary !== undefined) };
      })
      .filter((r) => r.count > 0);
    const headcount = roles.reduce((a, r) => a + r.count, 0);
    const monthly = roles.reduce((a, r) => a + r.monthly, 0);
    return { ...d, roles, headcount, monthly, annual: monthly * 12 };
  }).filter((d) => d.headcount > 0);

  const addedRoles = ((adjust && adjust.added) || []).filter((r) => N(r.count) > 0).map((r) => {
    const loaded = Math.round(N(r.salary) * costF);
    const count = Math.round(N(r.count));
    return { id: r.id, key: `added:${r.id}`, label: r.label || "Added role", count, gross: N(r.salary), loaded, monthly: count * loaded };
  });
  if (addedRoles.length) {
    const monthly = addedRoles.reduce((a, r) => a + r.monthly, 0);
    departments.push({
      id: "added", label: "Roles you added", operational: false, roles: addedRoles,
      formula: "Added manually during budget iteration",
      headcount: addedRoles.reduce((a, r) => a + r.count, 0), monthly, annual: monthly * 12,
    });
  }

  const customRoles = (cfg.custom_roles || []).filter((r) => N(r.count) > 0).map((r) => {
    const key = `custom:${r.id}`;
    const ov = (adjust && adjust.roles && adjust.roles[key]) || {};
    const removedC = !!(adjust && Array.isArray(adjust.removed) && adjust.removed.includes(key));
    let count = Math.round(N(r.count));
    if (ov.count !== undefined && ov.count !== "") count = Math.max(0, Math.round(N(ov.count)));
    if (removedC) count = 0;
    const g = ov.salary !== undefined && ov.salary !== "" ? N(ov.salary) : N(r.salary);
    const loaded = Math.round(g * costF);
    return { id: r.id, key, label: r.label || "Custom role", count, gross: g, loaded, monthly: count * loaded };
  }).filter((r) => r.count > 0);
  if (customRoles.length) {
    const monthly = customRoles.reduce((a, r) => a + r.monthly, 0);
    departments.push({
      id: "custom", label: "Additional roles", operational: false, roles: customRoles,
      formula: "Fixed headcount added by the Happy Retreats team (Admin → Staffing Calc → Salaries)",
      headcount: customRoles.reduce((a, r) => a + r.count, 0), monthly, annual: monthly * 12,
    });
  }

  const totalHeadcount = departments.reduce((a, d) => a + d.headcount, 0);
  const monthlyPayroll = departments.reduce((a, d) => a + d.monthly, 0);

  // Event-day staffing
  const Eg = N(inp.eventGuests) > 0 ? N(inp.eventGuests) : E;
  const eventRoles = (cfg.event_roles || []).map((r) => ({
    id: r.id, label: r.label, rate: N(r.day_rate),
    required: Eg > 0 ? Math.max(N(r.min), C(Eg / (N(r.per_guests) || 1))) : 0,
  }));
  const eventRequired = eventRoles.reduce((a, r) => a + r.required, 0);
  const deployBase = departments.filter((d) => ["front_office", "room_hk", "public_hk", "fnb", "kitchen"].includes(d.id)).reduce((a, d) => a + d.headcount, 0);
  const deployable = Math.min(eventRequired, Math.floor(deployBase * (N(f.deployable_pct) / 100)));
  const additionalTemp = Math.max(0, eventRequired - deployable);
  const reqDayCost = eventRoles.reduce((a, r) => a + r.required * r.rate, 0);
  const tempDayCost = eventRequired > 0 ? reqDayCost * (additionalTemp / eventRequired) : 0;
  const daysPerEvent = Math.max(1, N(inp.daysPerEvent) || 1);
  const costPerEvent = Math.round(tempDayCost * daysPerEvent);
  const eventsPerYear = N(inp.eventsPerYear);
  const annualTempCost = costPerEvent * eventsPerYear;

  // Financial impact
  const annualPayroll = monthlyPayroll * 12;
  const annualTotal = annualPayroll + annualTempCost;
  const revenue = N(inp.revenue);
  const pct = revenue > 0 ? (annualTotal / revenue) * 100 : null;
  const h = cfg.health;
  let status = null;
  if (pct !== null) {
    if (pct < N(h.lean_below)) status = { key: "lean", label: h.lean_label, tone: "sky" };
    else if (pct <= N(h.healthy_to)) status = { key: "healthy", label: h.healthy_label, tone: "emerald" };
    else if (pct <= N(h.review_to)) status = { key: "review", label: h.review_label, tone: "amber" };
    else status = { key: "unsustainable", label: h.unsustainable_label, tone: "red" };
  }

  return {
    E, Eg, capacityEstimated, factor, complexityLabel: opt.label,
    excludedRoles: [...excluded].map((id) => (cfg.roles[id] || {}).label).filter(Boolean),
    departments, totalHeadcount, monthlyPayroll, annualPayroll,
    event: { roles: eventRoles, required: eventRequired, deployable, deployBase, additionalTemp, costPerEvent, annualTempCost, daysPerEvent, eventsPerYear },
    finance: { annualTotal, revenue, pct, status },
  };
}
